import { tinycolor } from "@thebespokepixel/es-tinycolor";

/**
 *
 * @typedef {String} hexColor a string starting with a # followed by 6 hex characters
 */

/**
 * Adds CSS to the document
 * that defines custom CSS color variables for the given colors.
 * The variables look like --color-<name>, e.g. --color-primary-70.
 *
 * text/primary/highlight have additional color shades that are computed:
 * color (100), 90, 80, 70, 60, 50, 40, 20, 10 4.
 *
 * @param {object} [options]
 * @param {object} [options.colors] {<name>: <hex code>}
 * @param {hexColor} [options.colors.primary-70]
 * @param {hexColor} [options.colors.highlight-70]
 * @param {hexColor} [options.colors.text]
 * @param {hexColor} [options.colors.background]
 * @param {String} [options.selector] the CSS selector to wrap the variables in (default: :root)
 * @param {String} [options.styleElementSelector] A CSS selector pointing to a  <style> element (optional).
 *                         If present, the generated CSS is added to the given element.
 *                         Otherwise, a style tag is added to the document head.
 *                         This is useful when the CSS files with the default color variables
 *                         are not added to the head and the custom CSS needs to be
 *                         added to the document below the CSS files so that it actually
 *                         overrides the default colors.
 * @returns {void}
 */
export default function (options) {
  options = options || {};
  const colors = options.colors || {};
  const selector = options.selector || ":root";
  const styleTag = appendStyleTag(options.styleElementSelector);
  const colorShadeStyles = calculateColorStyles(colors);

  styleTag.innerHTML = `${selector} {
    ${colorShadeStyles.join("\n    ")}
    --link--similar-color-text-decoration: ${
      contrast(colors.text, colors["primary-70"] < 150) ? "underline" : "none"
    };
  }`;
}

function contrast(colorName1, colorName2) {
  if (colorName1 === undefined || colorName2 === undefined) {
    return false;
  }
  const color1 = tinycolor(colorName1).toRgb();
  const color2 = tinycolor(colorName2).toRgb();

  const contrast =
    Math.max(color1.r, color2.r) -
    Math.min(color1.r, color2.r) +
    (Math.max(color1.g, color2.g) - Math.min(color1.g, color2.g)) +
    (Math.max(color1.b, color2.b) - Math.min(color1.b, color2.b));
  return contrast;
}

function appendStyleTag(existingElementSelector) {
  if (existingElementSelector) {
    const element = document.querySelector(existingElementSelector);
    if (!element) {
      throw `No element found for styleElementSelector ${existingElementSelector}.`;
    }
    return element;
  }
  const styleTagId = "cobot-assets-custom-colors";
  const styleTag =
    document.querySelector(`head style#${styleTagId}`) ||
    document.createElement("style");
  if (!styleTag.id) {
    styleTag.id = styleTagId;
    document.head.appendChild(styleTag);
  }
  return styleTag;
}

function colorCssVariable(colorName, colorValue) {
  return `--color-${colorName}: ${colorValue.toHex8String()};`;
}

function defaultColor(name) {
  return tinycolor(
    {
      text: "#191d2f",
      background: "#ffffff",
      "primary-70": "#1628f4",
      "highlight-70": "#eb002f",
    }[name]
  );
}

function calculateColorStyles(colors) {
  const Shades = [90, 80, 70, 60, 50, 40, 20, 10, 4];
  const Base70Shades = {
    90: -50,
    80: -25,
    60: 14.286,
    50: 28.571,
    40: 42.857,
    20: 71.429,
    10: 85.714,
    4: 94.286,
  };

  return ["text", "background", "primary-70", "highlight-70"].map((name) => {
    const color = colors[name];
    const baseName = name.replace("-70", "");
    if (defaultColor(name).toHex8String() === color || color === undefined) {
      return colorCssVariable(baseName, defaultColor(name));
    }

    if (name === "background") {
      return colorCssVariable(name, tinycolor(color));
    }

    if (name.includes("-70")) {
      return [
        colorCssVariable(name, tinycolor(color)),
        colorCssVariable(baseName, brighten(color, -75)),
        ...Object.entries(Base70Shades).map(([shade, brightenValue]) => {
          return colorCssVariable(
            `${baseName}-${shade}`,
            brighten(color, brightenValue)
          );
        }),
      ].join("\n    ");
    } else {
      return [
        colorCssVariable(name, tinycolor(color)),
        ...Shades.map((shade) => {
          const brightenValue = 100 - shade;
          return colorCssVariable(
            `${baseName}-${shade}`,
            brighten(color, brightenValue)
          );
        }),
      ].join("\n    ");
    }
  });
}

/**
 * Brightens the given color by percentage.
 * taken from SASS scale-color function (https://github.com/sass/ruby-sass/blob/1ed4e31033dd0bff4da27d1f21c531e1fdaccaab/lib/sass/script/functions.rb#L1263)
 * @param {String} colorName any color that can be passed to tinycolor()
 * @param {number} percentage by how much to brigten
 * @return {TinyColor}
 * */
function brighten(colorName, percentage) {
  const color = tinycolor(colorName);
  const { h, s, l } = color.toHsl();
  const current = l;
  const scale = percentage / 100.0;
  const diff = scale > 0 ? 1.0 - current : current;
  const lNew = current + diff * scale;

  return tinycolor({ h, s, l: lNew });
}
